import { useEffect, useState, useRef } from 'react';

interface AnimatedCounterProps {
  end: number;
  duration?: number;
  suffix?: string;
  countDown?: boolean;
}

export const AnimatedCounter = ({ end, duration = 2000, suffix = '', countDown = false }: AnimatedCounterProps) => {
  const [count, setCount] = useState(countDown ? new Date().getFullYear() : 0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries && entries.length > 0 && entries[0]?.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const endTime = startTime + duration;
          const startValue = countDown ? new Date().getFullYear() : 0;

          const updateCount = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);
            const easeOutQuad = 1 - (1 - progress) * (1 - progress);

            let currentCount;
            if (countDown) {
              currentCount = Math.floor(startValue - (easeOutQuad * (startValue - end)));
            } else {
              currentCount = Math.floor(easeOutQuad * end);
            }

            setCount(currentCount);

            if (now < endTime) {
              requestAnimationFrame(updateCount);
            } else {
              setCount(end);
            }
          };

          requestAnimationFrame(updateCount);
        }
      },
      { threshold: 0.3 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated, countDown]);

  return <div ref={counterRef}>{count}{suffix}</div>;
};
